import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Sidebar from "../../Components/Sidebar/Sidebar";
import Header from "../../Components/Header/Header";
import classes from "./Staff.module.css";
import {
    FaUsers,
    FaUser,
    FaUserPlus,
    FaUserMd,
    FaUserNurse,
    FaHardHat,
    FaFileImport,
    FaFileExport,
    FaSearch,
    FaRedoAlt,
    FaEllipsisV,
    FaEye,
    FaEdit,
    FaTrash,
    FaMars,
    FaVenus,
    FaSpinner,
    FaSync,
    FaBriefcase,
    FaUserTie,
    FaUserCog,
    FaBuilding,
    FaCheckCircle,
    FaTimesCircle,
    FaHeart,
    FaClock,
    FaClipboardList,
    FaTimes,
    FaSave,
    FaUserCircle,
    FaEnvelope,
    FaPhone,
    FaCalendarAlt,
    FaMapMarkerAlt,
    FaVenusMars,
    FaIdCard,
    FaStethoscope,
    FaGraduationCap,
    FaExclamationTriangle,
    FaUndo,
    FaFlask,
    FaXRay,
    FaPills,
    FaTeeth,
    FaBrain,
    FaEye as FaEyeIcon,
    FaAmbulance,
    FaMicroscope,
    FaAppleAlt,
    FaLungs,
    FaSyringe,
    FaClipboardCheck,
} from "react-icons/fa";
import { BASE_URL } from "../../Components/API/Api";

// --- SAFE RENDER HELPERS ---
const safeString = (val, fallback = "N/A") => {
    if (val === null || val === undefined) return fallback;
    if (typeof val === "string") return val || fallback;
    if (typeof val === "number" || typeof val === "boolean") return String(val);
    if (typeof val === "object") {
        return (
            val.name ||
            val.title ||
            val.label ||
            val.department_name ||
            val.designation_name ||
            val.category ||
            val.value ||
            fallback
        );
    }
    return fallback;
};

const safeId = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === "object") return val.id ?? null;
    return val;
};

// --- SKELETON LOADER COMPONENTS ---
const SkeletonStatsCard = () => (
    <div className={classes.skeletonCard}>
        <div className={classes.skeletonIcon}></div>
        <div className={classes.skeletonCardInfo}>
            <div className={classes.skeletonText}></div>
            <div className={classes.skeletonText} style={{ width: "60%" }}></div>
            <div className={classes.skeletonText} style={{ width: "40%" }}></div>
        </div>
    </div>
);

const SkeletonRow = () => (
    <tr className={classes.skeletonRow}>
        <td>
            <div className={classes.skeletonStaffInfo}>
                <div className={classes.skeletonAvatar}></div>
                <div>
                    <div className={classes.skeletonText} style={{ width: "120px" }}></div>
                    <div className={classes.skeletonText} style={{ width: "80px" }}></div>
                </div>
            </div>
        </td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonBadge}></div></td>
        <td>
            <div className={classes.skeletonActions}>
                <div className={classes.skeletonActionBtn}></div>
                <div className={classes.skeletonActionBtn}></div>
                <div className={classes.skeletonActionBtn}></div>
            </div>
        </td>
    </tr>
);

function Staff() {
    const navigate = useNavigate();

    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("");
    const [department, setDepartment] = useState("");
    const [status, setStatus] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [stats, setStats] = useState([
        { title: "Total Staff", value: "0", link: "View all staff", icon: <FaUsers />, color: "#2F80ED", bg: "#EDF5FF" },
        { title: "Medical / Clinical", value: "0", link: "View medical staff", icon: <FaUserMd />, color: "#27AE60", bg: "#ECFFF5" },
        { title: "Administrative", value: "0", link: "View admin staff", icon: <FaUserTie />, color: "#9B51E0", bg: "#F7EEFF" },
        { title: "Support Staff", value: "0", link: "View support staff", icon: <FaHardHat />, color: "#F2994A", bg: "#FFF6EB" },
        { title: "Active", value: "0", link: "View active staff", icon: <FaCheckCircle />, color: "#27AE60", bg: "#ECFFF5" },
    ]);

    // ---- MODAL STATE ----
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [modalError, setModalError] = useState("");

    const rowsPerPage = 10;

    // Fetch staff
    const fetchStaff = async () => {
        setLoading(true);
        setError("");
        try {
            const companyId = localStorage.getItem("companyId") || "1";
            const response = await axios.get(`${BASE_URL}/api/staff`, {
                params: { company_id: parseInt(companyId), per_page: 100 },
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                },
            });

            let staffData = [];
            if (response.data && response.data.data) {
                if (Array.isArray(response.data.data)) staffData = response.data.data;
                else if (response.data.data.data) staffData = response.data.data.data;
                else if (response.data.data.staff) staffData = response.data.data.staff;
                else staffData = [response.data.data];
            }

            setStaff(staffData);
            updateStats(staffData);
        } catch (error) {
            console.error("Error fetching staff:", error);
            let errorMsg = "Failed to load staff. Please try again.";
            if (error.response) {
                if (error.response.status === 401) errorMsg = "Unauthorized. Please login again.";
                else if (error.response.data?.message) {
                    const message = error.response.data.message;
                    if (typeof message === "object") {
                        const firstKey = Object.keys(message)[0];
                        errorMsg = Array.isArray(message[firstKey]) ? message[firstKey][0] : JSON.stringify(message);
                    } else if (typeof message === "string") errorMsg = message;
                }
            } else if (error.request) {
                errorMsg = "Unable to connect to server. Please check your internet connection.";
            }
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const updateStats = (staffData) => {
        const total = staffData.length;
        const medical = staffData.filter(s => safeString(s.staff_category, "") === "Medical / Clinical Staff").length;
        const admin = staffData.filter(s => safeString(s.staff_category, "") === "Non-Medical / Administrative Staff").length;
        const support = staffData.filter(s => safeString(s.staff_category, "") === "Support Staff").length;
        const active = staffData.filter(s => safeString(s.status, "").toLowerCase() === "active").length;

        setStats([
            { title: "Total Staff", value: total.toString(), link: "View all staff", icon: <FaUsers />, color: "#2F80ED", bg: "#EDF5FF" },
            { title: "Medical / Clinical", value: medical.toString(), link: "View medical staff", icon: <FaUserMd />, color: "#27AE60", bg: "#ECFFF5" },
            { title: "Administrative", value: admin.toString(), link: "View admin staff", icon: <FaUserTie />, color: "#9B51E0", bg: "#F7EEFF" },
            { title: "Support Staff", value: support.toString(), link: "View support staff", icon: <FaHardHat />, color: "#F2994A", bg: "#FFF6EB" },
            { title: "Active", value: active.toString(), link: "View active staff", icon: <FaCheckCircle />, color: "#27AE60", bg: "#ECFFF5" },
        ]);
    };

    useEffect(() => {
        fetchStaff();
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
        } catch {
            return dateString;
        }
    };

    const getAvatar = (member) => {
        if (member.avatar) return member.avatar;
        if (member.profile_photo) return member.profile_photo;
        const gender = safeString(member.gender, "").toLowerCase() === "female" ? "women" : "men";
        const id = safeId(member.id) || Math.floor(Math.random() * 100);
        return `https://randomuser.me/api/portraits/${gender}/${id % 70 + 1}.jpg`;
    };

    const getInitials = (name) => {
        if (!name) return "?";
        const parts = name.split(" ");
        if (parts.length >= 2) return parts[0][0] + parts[1][0];
        return name.substring(0, 2).toUpperCase();
    };

    const getFullName = (member) => {
        const firstName = member.first_name || "";
        const middleName = member.middle_name || "";
        const lastName = member.last_name || "";
        const name = member.name || "";
        if (name) return name;
        return `${firstName} ${middleName} ${lastName}`.trim().replace(/\s+/g, " ") || "Unknown";
    };

    const categories = [...new Set(staff.map(s => safeString(s.staff_category, "")).filter(Boolean))];
    const departments = [...new Set(staff.map(s => safeString(s.department, "")).filter(Boolean))];
    const statuses = [...new Set(staff.map(s => safeString(s.status, "")).filter(Boolean))];

    const filteredStaff = staff.filter((member) => {
        const keyword = search.toLowerCase();
        const fullName = getFullName(member).toLowerCase();

        const searchMatch =
            (member.first_name || "").toLowerCase().includes(keyword) ||
            (member.last_name || "").toLowerCase().includes(keyword) ||
            (member.email || "").toLowerCase().includes(keyword) ||
            (member.phone_number || "").toLowerCase().includes(keyword) ||
            (member.staff_id || "").toLowerCase().includes(keyword) ||
            fullName.includes(keyword);

        const categoryMatch = category === "" || safeString(member.staff_category, "") === category;
        const departmentMatch = department === "" || safeString(member.department, "") === department;
        const statusMatch = status === "" || safeString(member.status, "") === status;

        return searchMatch && categoryMatch && departmentMatch && statusMatch;
    });

    const totalPages = Math.ceil(filteredStaff.length / rowsPerPage);
    const indexOfLast = currentPage * rowsPerPage;
    const indexOfFirst = indexOfLast - rowsPerPage;
    const currentStaff = filteredStaff.slice(indexOfFirst, indexOfLast);

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) setCurrentPage(page);
    };

    const resetFilters = () => {
        setSearch("");
        setCategory("");
        setDepartment("");
        setStatus("");
        setCurrentPage(1);
    };

    const getStatusClass = (status) => {
        const s = safeString(status, "").toLowerCase();
        if (!s) return classes.statusInactive;
        if (s === "active") return classes.statusActive;
        if (s === "on leave" || s === "on_leave") return classes.statusOnLeave;
        if (s === "probation") return classes.statusProbation;
        if (s === "suspended") return classes.statusSuspended;
        if (s === "terminated" || s === "resigned") return classes.statusTerminated;
        return classes.statusInactive;
    };

    const getStaffTypeIcon = (type) => {
        const t = safeString(type, "");
        if (!t) return <FaUser />;
        switch (t) {
            case "Doctor / Physician":
            case "Doctor":                              return <FaUserMd />;
            case "Dentist":                             return <FaTeeth />;
            case "Nurse":                               return <FaUserNurse />;
            case "Midwife":                             return <FaUserNurse />;
            case "Pharmacist":                          return <FaPills />;
            case "Medical Laboratory Staff":            return <FaMicroscope />;
            case "Radiology / Imaging Staff":           return <FaXRay />;
            case "Physiotherapist":                     return <FaUserMd />;
            case "Occupational Therapist":              return <FaUserMd />;
            case "Dietitian / Nutritionist":            return <FaAppleAlt />;
            case "Psychologist":                        return <FaBrain />;
            case "Optometrist":                         return <FaEyeIcon />;
            case "Paramedic / Emergency Medical Staff": return <FaAmbulance />;
            case "Medical Social Worker":               return <FaUser />;
            case "Respiratory Therapist":               return <FaLungs />;
            case "Anaesthesia Staff":                   return <FaSyringe />;
            case "Public Health Staff":                 return <FaClipboardCheck />;
            case "Other Clinical Staff":                return <FaStethoscope />;

            case "Hospital Administration":
            case "Human Resources":
            case "Finance / Accounting":
            case "Procurement":
            case "Billing / Revenue":
            case "Legal Services":                      return <FaUserTie />;
            case "Reception / Front Desk":
            case "Customer Service":
            case "Secretarial / Executive Assistant":   return <FaUser />;
            case "Medical Records / Health Information":return <FaClipboardList />;
            case "Information Technology":              return <FaUserCog />;
            case "Public Relations / Communications":   return <FaUsers />;
            case "Quality Assurance":                   return <FaClipboardCheck />;
            case "Research / Data Management":          return <FaFlask />;
            case "Other Administrative Staff":          return <FaUserTie />;

            case "Security":                            return <FaUser />;
            case "Cleaning / Environmental Services":   return <FaUser />;
            case "Maintenance":                         return <FaUserCog />;
            case "Transport / Drivers":                 return <FaUser />;
            case "Laundry / Linen Services":            return <FaUser />;
            case "Catering / Food Services":            return <FaAppleAlt />;
            case "Biomedical Engineering / Equipment Support": return <FaUserCog />;
            case "Stores / Inventory":                  return <FaBuilding />;
            case "Waste Management":                    return <FaUser />;
            case "Porter / Patient Transport":          return <FaUser />;
            case "Mortuary Services":                   return <FaUser />;
            case "Facilities Management":               return <FaBuilding />;
            case "Electrical Services":                 return <FaUserCog />;
            case "Plumbing Services":                   return <FaUserCog />;
            case "Other Support Staff":                 return <FaHardHat />;

            default:                                    return <FaUser />;
        }
    };

    // ---------------------------------------------------------------
    // MODAL OPENERS
    // ---------------------------------------------------------------
    const openViewModal = (member) => {
        setSelectedMember(member);
        setModalError("");
        setViewModalOpen(true);
    };

    const openEditModal = (member) => {
        setSelectedMember(member);
        setModalError("");
        setEditForm({
            first_name: member.first_name || "",
            middle_name: member.middle_name || "",
            last_name: member.last_name || "",
            email: member.email || "",
            phone: member.phone || member.phone_number || "",
            phone_country_code: member.phone_country_code || "+234",
            phone_alternative: member.phone_alternative || "",
            date_of_birth: member.date_of_birth ? member.date_of_birth.split("T")[0] : "",
            gender: safeString(member.gender, "").toLowerCase(),
            marital_status: safeString(member.marital_status, ""),
            nationality: safeString(member.nationality, ""),
            address: member.address || "",
            city: member.city || "",
            state: safeString(member.state, ""),
            country: safeString(member.country, ""),
            postal_code: member.postal_code || "",
            emergency_contact_name: member.emergency_contact_name || "",
            emergency_relationship: member.emergency_relationship || "",
            emergency_phone: member.emergency_phone || "",
            notes: member.notes || "",
            status: safeString(member.status, "").toLowerCase(),
        });
        setEditModalOpen(true);
    };

    const openDeleteModal = (member) => {
        setSelectedMember(member);
        setModalError("");
        setDeleteModalOpen(true);
    };

    const closeAllModals = () => {
        if (isSaving || isDeleting) return;
        setViewModalOpen(false);
        setEditModalOpen(false);
        setDeleteModalOpen(false);
        setSelectedMember(null);
        setEditForm({});
        setModalError("");
    };

    // ---------------------------------------------------------------
    // EDIT SAVE  →  POST /api/staff/update
    // ---------------------------------------------------------------
    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditForm((prev) => ({ ...prev, [name]: value }));
        setModalError("");
    };

    const handleSaveEdit = async () => {
        if (!selectedMember) return;

        const id = safeId(selectedMember.id);
        if (!id) {
            setModalError("Cannot update — missing staff ID.");
            return;
        }

        const errors = [];
        if (!editForm.first_name) errors.push("First Name");
        if (!editForm.last_name) errors.push("Last Name");
        if (!editForm.email) errors.push("Email");

        if (errors.length > 0) {
            setModalError(`Please fill in: ${errors.join(", ")}`);
            return;
        }

        setIsSaving(true);
        setModalError("");

        try {
            const companyId = parseInt(localStorage.getItem("companyId") || "1");

            // Extract primitives from selectedMember
            const origDeptId =
                typeof selectedMember.department === "object"
                    ? selectedMember.department?.id
                    : selectedMember.department_id;
            const origDesigId =
                typeof selectedMember.designation === "object"
                    ? selectedMember.designation?.id
                    : selectedMember.designation_id;

            // Helper: fall back to a non-empty placeholder
            const orDefault = (val, def = "") =>
                val === null || val === undefined || val === "" ? def : val;

            const payload = {
                id: id,
                company_id: companyId,

                // Editable — from form
                first_name: editForm.first_name,
                last_name: editForm.last_name,
                email: editForm.email,

                // Required — from original record
                employee_no: orDefault(
                    selectedMember.employee_no || selectedMember.staff_id
                ),
                designation_id: origDesigId ?? null,
                staff_type: orDefault(selectedMember.staff_type),
                staff_category: orDefault(selectedMember.staff_category),
                employment_date: orDefault(selectedMember.employment_date),
                employment_type: orDefault(selectedMember.employment_type),

                // Editable optional — from form
                middle_name: editForm.middle_name || null,
                phone: editForm.phone || null,
                phone_country_code: editForm.phone_country_code || null,
                phone_alternative: editForm.phone_alternative || null,
                date_of_birth: editForm.date_of_birth || null,
                gender: editForm.gender || null,
                marital_status: editForm.marital_status || null,
                nationality: editForm.nationality || null,
                address: editForm.address || null,
                city: editForm.city || null,
                state: editForm.state || null,
                country: editForm.country || null,
                postal_code: editForm.postal_code || null,
                emergency_contact_name: editForm.emergency_contact_name || null,
                emergency_relationship: editForm.emergency_relationship || null,
                emergency_phone: editForm.emergency_phone || null,
                notes: editForm.notes || null,
                status: editForm.status || null,

                // From original record
                department_id: origDeptId ?? null,
                reporting_to: selectedMember.reporting_to ?? null,
                work_shift: selectedMember.work_shift || null,
                emergency_phone_country_code:
                    selectedMember.emergency_phone_country_code || null,

                // ============================================================
                // Medical fields — backend marks these `required` with
                // `>= 1` minimums, so we must send valid non-null values.
                // ============================================================
                specialization: orDefault(selectedMember.specialization, "N/A"),
                sub_specialization:
                    selectedMember.sub_specialization ?? null,
                qualification: orDefault(selectedMember.qualification, "N/A"),
                license_number: orDefault(selectedMember.license_number, "N/A"),
                registration_number:
                    selectedMember.registration_number ?? null,
                experience_years: orDefault(
                    selectedMember.experience_years,
                    1
                ),
                consultation_fee: orDefault(
                    selectedMember.consultation_fee,
                    1                 // ← API requires >= 1, so 0 fails
                ),
                languages_spoken: Array.isArray(selectedMember.languages_spoken)
                    ? selectedMember.languages_spoken
                    : [],
                about: selectedMember.about ?? null,
            };

            console.log("Updating staff with payload:", payload);

            const response = await axios.post(
                `${BASE_URL}/api/staff/update`,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            console.log("Staff update response:", response.data);

            Swal.fire({
                icon: "success",
                title: "Updated!",
                text: "Staff member updated successfully.",
                timer: 1500,
                showConfirmButton: false,
                timerProgressBar: true,
            });

            closeAllModals();
            fetchStaff();
        } catch (error) {
            console.error("Error updating staff:", error);
            let msg = "Failed to update staff member.";

            if (error.response) {
                if (error.response.status === 401) {
                    msg = "Unauthorized. Please login again.";
                } else if (
                    error.response.status === 422 &&
                    error.response.data?.errors
                ) {
                    const errs = error.response.data.errors;
                    const firstKey = Object.keys(errs)[0];
                    msg = `${firstKey}: ${errs[firstKey][0]}`;
                } else if (error.response.data?.message) {
                    const message = error.response.data.message;
                    if (typeof message === "object") {
                        const firstKey = Object.keys(message)[0];
                        msg = Array.isArray(message[firstKey])
                            ? message[firstKey][0]
                            : JSON.stringify(message);
                    } else if (typeof message === "string") {
                        msg = message;
                    }
                }
            } else if (error.request) {
                msg = "Unable to connect to server.";
            }
            setModalError(msg);
        } finally {
            setIsSaving(false);
        }
    };

    // ---------------------------------------------------------------
    // DELETE
    // ---------------------------------------------------------------
    const handleConfirmDelete = async () => {
        if (!selectedMember) return;
        const id = safeId(selectedMember.id);
        if (!id) {
            setModalError("Cannot delete — missing staff ID.");
            return;
        }

        setIsDeleting(true);
        setModalError("");

        try {
            await axios.delete(`${BASE_URL}/api/staff/${id}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                },
            });

            Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: "Staff member deleted successfully.",
                timer: 1500,
                showConfirmButton: false,
                timerProgressBar: true,
            });

            closeAllModals();
            fetchStaff();
        } catch (error) {
            console.error("Error deleting staff:", error);
            let msg = "Failed to delete staff member.";
            if (error.response?.data?.message) {
                const message = error.response.data.message;
                msg = typeof message === "string" ? message : "Failed to delete.";
            } else if (error.request) {
                msg = "Unable to connect to server.";
            }
            setModalError(msg);
        } finally {
            setIsDeleting(false);
        }
    };

    // ---------------------------------------------------------------
    // RENDER HELPERS FOR MODALS
    // ---------------------------------------------------------------
    const renderInfoRow = (icon, label, value) => (
        <div className={classes.infoRow}>
            <div className={classes.infoIcon}>{icon}</div>
            <div className={classes.infoContent}>
                <span className={classes.infoLabel}>{label}</span>
                <span className={classes.infoValue}>{safeString(value)}</span>
            </div>
        </div>
    );

    const renderSkeletonRows = () =>
        Array(5).fill(0).map((_, index) => <SkeletonRow key={`skeleton-${index}`} />);

    const renderSkeletonStats = () =>
        Array(5).fill(0).map((_, index) => <SkeletonStatsCard key={`skeleton-stat-${index}`} />);

    return (
        <div className={classes.page}>
            <Sidebar />
            <div className={classes.main}>
                <Header />
                <div className={classes.content}>
                    {/* TOP SECTION */}
                    <div className={classes.top}>
                        <div>
                            <h2>Staff</h2>
                            <div className={classes.breadcrumb}>
                                Dashboard <span>&gt;</span> Staff
                            </div>
                        </div>
                        <div className={classes.actions}>
                            <button className={classes.outline}><FaFileImport /> Import</button>
                            <button className={classes.outline}><FaFileExport /> Export</button>
                            <button className={classes.primary} onClick={() => navigate("/addStaff")}>
                                + Add New Staff
                            </button>
                        </div>
                    </div>

                    {/* STATS CARDS */}
                    <div className={classes.cards}>
                        {loading ? renderSkeletonStats() : (
                            stats.map((item, index) => (
                                <div className={classes.card} key={index}>
                                    <div className={classes.icon} style={{ background: item.bg, color: item.color }}>
                                        {item.icon}
                                    </div>
                                    <div className={classes.cardInfo}>
                                        <p>{item.title}</p>
                                        <h2>{item.value}</h2>
                                        <button>{item.link}</button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* SEARCH & FILTERS */}
                    <div className={classes.searchContainer}>
                        <div className={classes.searchBox}>
                            <FaSearch className={classes.searchIcon} />
                            <input
                                type="text"
                                placeholder="Search by Name, Email, Staff ID..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                disabled={loading}
                            />
                        </div>

                        <select value={category} onChange={(e) => setCategory(e.target.value)} disabled={loading}>
                            <option value="">All Categories</option>
                            {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                        </select>

                        <select value={department} onChange={(e) => setDepartment(e.target.value)} disabled={loading}>
                            <option value="">All Departments</option>
                            {departments.map((dept) => <option key={dept} value={dept}>{dept}</option>)}
                        </select>

                        <select value={status} onChange={(e) => setStatus(e.target.value)} disabled={loading}>
                            <option value="">All Status</option>
                            {statuses.map((stat) => <option key={stat} value={stat}>{stat}</option>)}
                        </select>

                        <button className={classes.resetBtn} onClick={resetFilters} disabled={loading}>
                            <FaRedoAlt /> Reset
                        </button>
                    </div>

                    {/* ERROR */}
                    {error && (
                        <div className={classes.errorMessage}>
                            <span>{error}</span>
                            <button onClick={fetchStaff} className={classes.retryBtn}>Retry</button>
                        </div>
                    )}

                    {/* TABLE */}
                    <div className={classes.tableContainer}>
                        <table className={classes.table}>
                            <thead>
                                <tr>
                                    <th>Staff Member</th>
                                    <th>Staff ID</th>
                                    <th>Staff Type</th>
                                    <th>Department</th>
                                    <th>Designation</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? renderSkeletonRows() : currentStaff.length > 0 ? (
                                    currentStaff.map((member) => {
                                        const fullName = getFullName(member);
                                        const avatar = getAvatar(member);
                                        const rowId = safeId(member.id);

                                        return (
                                            <tr key={rowId ?? fullName}>
                                                <td>
                                                    <div className={classes.staffInfo}>
                                                        <img
                                                            src={avatar}
                                                            alt={fullName}
                                                            className={classes.avatar}
                                                            onError={(e) => {
                                                                e.target.style.display = "none";
                                                                if (e.target.nextSibling) e.target.nextSibling.style.display = "flex";
                                                            }}
                                                        />
                                                        <div className={classes.avatarFallback} style={{ display: "none" }}>
                                                            {getInitials(fullName)}
                                                        </div>
                                                        <div>
                                                            <h4>{fullName}</h4>
                                                            <span>{safeString(member.email)}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{safeString(member.staff_id || member.employee_no)}</td>
                                                <td>
                                                    <div className={classes.categoryCell}>
                                                        <span className={classes.categoryIcon}>
                                                            {getStaffTypeIcon(member.staff_type)}
                                                        </span>
                                                        <span>{safeString(member.staff_type)}</span>
                                                    </div>
                                                </td>
                                                <td>{safeString(member.department)}</td>
                                                <td>{safeString(member.designation)}</td>
                                                <td>
                                                    <span className={`${classes.badge} ${getStatusClass(member.status)}`}>
                                                        {safeString(member.status)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className={classes.tableActions}>
                                                        <button
                                                            onClick={() => openViewModal(member)}
                                                            className={classes.viewBtn}
                                                            title="View"
                                                        >
                                                            <FaEye />
                                                        </button>
                                                        <button
                                                            onClick={() => openEditModal(member)}
                                                            className={classes.editBtn}
                                                            title="Edit"
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            onClick={() => openDeleteModal(member)}
                                                            className={classes.deleteBtn}
                                                            title="Delete"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: "center", padding: "40px" }}>
                                            No Staff Found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* PAGINATION */}
                    {!loading && filteredStaff.length > 0 && (
                        <div className={classes.pagination}>
                            <p>
                                Showing
                                <strong> {filteredStaff.length > 0 ? indexOfFirst + 1 : 0}-{Math.min(indexOfLast, filteredStaff.length)}</strong>
                                {" "}of <strong>{filteredStaff.length}</strong> staff members
                            </p>
                            <div className={classes.pages}>
                                <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
                                    Previous
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => goToPage(i + 1)}
                                        className={currentPage === i + 1 ? classes.activePage : ""}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                                <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ================= VIEW MODAL ================= */}
            {viewModalOpen && selectedMember && (
                <div className={classes.modalOverlay} onClick={closeAllModals}>
                    <div className={classes.modalLarge} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.modalHeader}>
                            <div className={classes.modalHeaderLeft}>
                                <div className={classes.modalAvatarWrapper}>
                                    <img
                                        src={getAvatar(selectedMember)}
                                        alt={getFullName(selectedMember)}
                                        className={classes.modalAvatar}
                                    />
                                </div>
                                <div>
                                    <h3>{getFullName(selectedMember)}</h3>
                                    <p>{safeString(selectedMember.staff_id || selectedMember.employee_no)}</p>
                                    <span className={`${classes.badge} ${getStatusClass(selectedMember.status)}`}>
                                        {safeString(selectedMember.status)}
                                    </span>
                                </div>
                            </div>
                            <button className={classes.modalCloseBtn} onClick={closeAllModals}>
                                <FaTimes />
                            </button>
                        </div>

                        <div className={classes.modalBody}>
                            {/* Contact */}
                            <div className={classes.modalSection}>
                                <h4 className={classes.modalSectionTitle}>Contact Information</h4>
                                <div className={classes.infoGrid}>
                                    {renderInfoRow(<FaEnvelope />, "Email", selectedMember.email)}
                                    {renderInfoRow(<FaPhone />, "Phone", selectedMember.phone || selectedMember.phone_number)}
                                    {renderInfoRow(<FaPhone />, "Alt. Phone", selectedMember.phone_alternative)}
                                    {renderInfoRow(<FaMapMarkerAlt />, "Address", selectedMember.address)}
                                    {renderInfoRow(<FaMapMarkerAlt />, "City", selectedMember.city)}
                                    {renderInfoRow(<FaMapMarkerAlt />, "State", selectedMember.state)}
                                    {renderInfoRow(<FaMapMarkerAlt />, "Country", selectedMember.country)}
                                </div>
                            </div>

                            {/* Personal */}
                            <div className={classes.modalSection}>
                                <h4 className={classes.modalSectionTitle}>Personal Information</h4>
                                <div className={classes.infoGrid}>
                                    {renderInfoRow(<FaCalendarAlt />, "Date of Birth", formatDate(selectedMember.date_of_birth))}
                                    {renderInfoRow(<FaVenusMars />, "Gender", selectedMember.gender)}
                                    {renderInfoRow(<FaHeart />, "Marital Status", selectedMember.marital_status)}
                                    {renderInfoRow(<FaIdCard />, "Nationality", selectedMember.nationality)}
                                </div>
                            </div>

                            {/* Employment */}
                            <div className={classes.modalSection}>
                                <h4 className={classes.modalSectionTitle}>Employment Information</h4>
                                <div className={classes.infoGrid}>
                                    {renderInfoRow(<FaBriefcase />, "Category", selectedMember.staff_category)}
                                    {renderInfoRow(<FaUserMd />, "Type", selectedMember.staff_type)}
                                    {renderInfoRow(<FaBuilding />, "Department", selectedMember.department)}
                                    {renderInfoRow(<FaUserTie />, "Designation", selectedMember.designation)}
                                    {renderInfoRow(<FaCalendarAlt />, "Employment Date", formatDate(selectedMember.employment_date))}
                                    {renderInfoRow(<FaClock />, "Employment Type", selectedMember.employment_type)}
                                    {renderInfoRow(<FaClock />, "Work Shift", selectedMember.work_shift)}
                                </div>
                            </div>

                            {/* Professional (medical only) */}
                            {(selectedMember.qualification || selectedMember.license_number) && (
                                <div className={classes.modalSection}>
                                    <h4 className={classes.modalSectionTitle}>Professional Information</h4>
                                    <div className={classes.infoGrid}>
                                        {renderInfoRow(<FaGraduationCap />, "Qualification", selectedMember.qualification)}
                                        {renderInfoRow(<FaStethoscope />, "Specialization", selectedMember.specialization)}
                                        {renderInfoRow(<FaIdCard />, "License Number", selectedMember.license_number)}
                                        {renderInfoRow(<FaIdCard />, "Registration Number", selectedMember.registration_number)}
                                        {renderInfoRow(<FaBriefcase />, "Years of Experience", selectedMember.experience_years)}
                                    </div>
                                </div>
                            )}

                            {/* Emergency */}
                            <div className={classes.modalSection}>
                                <h4 className={classes.modalSectionTitle}>Emergency Contact</h4>
                                <div className={classes.infoGrid}>
                                    {renderInfoRow(<FaUser />, "Name", selectedMember.emergency_contact_name)}
                                    {renderInfoRow(<FaUsers />, "Relationship", selectedMember.emergency_relationship)}
                                    {renderInfoRow(<FaPhone />, "Phone", selectedMember.emergency_phone)}
                                </div>
                            </div>

                            {selectedMember.notes && (
                                <div className={classes.modalSection}>
                                    <h4 className={classes.modalSectionTitle}>Notes</h4>
                                    <p className={classes.modalNotes}>{selectedMember.notes}</p>
                                </div>
                            )}
                        </div>

                        <div className={classes.modalFooter}>
                            <button className={classes.modalBtnGhost} onClick={closeAllModals}>
                                Close
                            </button>
                            <button
                                className={classes.modalBtnPrimary}
                                onClick={() => {
                                    closeAllModals();
                                    setTimeout(() => openEditModal(selectedMember), 100);
                                }}
                            >
                                <FaEdit /> Edit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ================= EDIT MODAL ================= */}
            {editModalOpen && selectedMember && (
                <div className={classes.modalOverlay} onClick={closeAllModals}>
                    <div className={classes.modalLarge} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.modalHeader}>
                            <div className={classes.modalHeaderLeft}>
                                <div className={classes.modalAvatarWrapper}>
                                    <img src={getAvatar(selectedMember)} alt="Profile" className={classes.modalAvatar} />
                                </div>
                                <div>
                                    <h3>Edit Staff</h3>
                                    <p>{getFullName(selectedMember)}</p>
                                </div>
                            </div>
                            <button className={classes.modalCloseBtn} onClick={closeAllModals} disabled={isSaving}>
                                <FaTimes />
                            </button>
                        </div>

                        <div className={classes.modalBody}>
                            {modalError && (
                                <div className={classes.modalError}>
                                    <FaExclamationTriangle /> {modalError}
                                </div>
                            )}

                            {/* Personal */}
                            <div className={classes.modalSection}>
                                <h4 className={classes.modalSectionTitle}>Personal Information</h4>
                                <div className={classes.editGrid3}>
                                    <div className={classes.modalInputGroup}>
                                        <label>First Name <span className={classes.required}>*</span></label>
                                        <input
                                            type="text"
                                            name="first_name"
                                            value={editForm.first_name || ""}
                                            onChange={handleEditChange}
                                            disabled={isSaving}
                                        />
                                    </div>
                                    <div className={classes.modalInputGroup}>
                                        <label>Middle Name</label>
                                        <input
                                            type="text"
                                            name="middle_name"
                                            value={editForm.middle_name || ""}
                                            onChange={handleEditChange}
                                            disabled={isSaving}
                                        />
                                    </div>
                                    <div className={classes.modalInputGroup}>
                                        <label>Last Name <span className={classes.required}>*</span></label>
                                        <input
                                            type="text"
                                            name="last_name"
                                            value={editForm.last_name || ""}
                                            onChange={handleEditChange}
                                            disabled={isSaving}
                                        />
                                    </div>
                                </div>

                                <div className={classes.editGrid2}>
                                    <div className={classes.modalInputGroup}>
                                        <label>Email <span className={classes.required}>*</span></label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={editForm.email || ""}
                                            onChange={handleEditChange}
                                            disabled={isSaving}
                                        />
                                    </div>
                                    <div className={classes.modalInputGroup}>
                                        <label>Phone</label>
                                        <input
                                            type="text"
                                            name="phone"
                                            value={editForm.phone || ""}
                                            onChange={handleEditChange}
                                            disabled={isSaving}
                                        />
                                    </div>
                                </div>

                                <div className={classes.editGrid2}>
                                    <div className={classes.modalInputGroup}>
                                        <label>Date of Birth</label>
                                        <input
                                            type="date"
                                            name="date_of_birth"
                                            value={editForm.date_of_birth || ""}
                                            onChange={handleEditChange}
                                            disabled={isSaving}
                                        />
                                    </div>
                                    <div className={classes.modalInputGroup}>
                                        <label>Gender</label>
                                        <select
                                            name="gender"
                                            value={editForm.gender || ""}
                                            onChange={handleEditChange}
                                            disabled={isSaving}
                                        >
                                            <option value="">Select gender</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                </div>

                                <div className={classes.editGrid2}>
                                    <div className={classes.modalInputGroup}>
                                        <label>Marital Status</label>
                                        <select
                                            name="marital_status"
                                            value={editForm.marital_status || ""}
                                            onChange={handleEditChange}
                                            disabled={isSaving}
                                        >
                                            <option value="">Select</option>
                                            <option>Single</option>
                                            <option>Married</option>
                                            <option>Divorced</option>
                                            <option>Widowed</option>
                                        </select>
                                    </div>
                                    <div className={classes.modalInputGroup}>
                                        <label>Nationality</label>
                                        <input
                                            type="text"
                                            name="nationality"
                                            value={editForm.nationality || ""}
                                            onChange={handleEditChange}
                                            disabled={isSaving}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Contact */}
                            <div className={classes.modalSection}>
                                <h4 className={classes.modalSectionTitle}>Contact & Address</h4>
                                <div className={classes.modalInputGroup}>
                                    <label>Address</label>
                                    <input
                                        type="text"
                                        name="address"
                                        value={editForm.address || ""}
                                        onChange={handleEditChange}
                                        disabled={isSaving}
                                    />
                                </div>
                                <div className={classes.editGrid3}>
                                    <div className={classes.modalInputGroup}>
                                        <label>City</label>
                                        <input
                                            type="text"
                                            name="city"
                                            value={editForm.city || ""}
                                            onChange={handleEditChange}
                                            disabled={isSaving}
                                        />
                                    </div>
                                    <div className={classes.modalInputGroup}>
                                        <label>State</label>
                                        <input
                                            type="text"
                                            name="state"
                                            value={editForm.state || ""}
                                            onChange={handleEditChange}
                                            disabled={isSaving}
                                        />
                                    </div>
                                    <div className={classes.modalInputGroup}>
                                        <label>Country</label>
                                        <input
                                            type="text"
                                            name="country"
                                            value={editForm.country || ""}
                                            onChange={handleEditChange}
                                            disabled={isSaving}
                                        />
                                    </div>
                                </div>
                                <div className={classes.editGrid2}>
                                    <div className={classes.modalInputGroup}>
                                        <label>Postal Code</label>
                                        <input
                                            type="text"
                                            name="postal_code"
                                            value={editForm.postal_code || ""}
                                            onChange={handleEditChange}
                                            disabled={isSaving}
                                        />
                                    </div>
                                    <div className={classes.modalInputGroup}>
                                        <label>Alternative Phone</label>
                                        <input
                                            type="text"
                                            name="phone_alternative"
                                            value={editForm.phone_alternative || ""}
                                            onChange={handleEditChange}
                                            disabled={isSaving}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Emergency */}
                            <div className={classes.modalSection}>
                                <h4 className={classes.modalSectionTitle}>Emergency Contact</h4>
                                <div className={classes.editGrid3}>
                                    <div className={classes.modalInputGroup}>
                                        <label>Name</label>
                                        <input
                                            type="text"
                                            name="emergency_contact_name"
                                            value={editForm.emergency_contact_name || ""}
                                            onChange={handleEditChange}
                                            disabled={isSaving}
                                        />
                                    </div>
                                    <div className={classes.modalInputGroup}>
                                        <label>Relationship</label>
                                        <input
                                            type="text"
                                            name="emergency_relationship"
                                            value={editForm.emergency_relationship || ""}
                                            onChange={handleEditChange}
                                            disabled={isSaving}
                                        />
                                    </div>
                                    <div className={classes.modalInputGroup}>
                                        <label>Phone</label>
                                        <input
                                            type="text"
                                            name="emergency_phone"
                                            value={editForm.emergency_phone || ""}
                                            onChange={handleEditChange}
                                            disabled={isSaving}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Status + Notes */}
                            <div className={classes.modalSection}>
                                <h4 className={classes.modalSectionTitle}>Status & Notes</h4>
                                <div className={classes.editGrid2}>
                                    <div className={classes.modalInputGroup}>
                                        <label>Status</label>
                                        <select
                                            name="status"
                                            value={editForm.status || ""}
                                            onChange={handleEditChange}
                                            disabled={isSaving}
                                        >
                                            <option value="">Select status</option>
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                            <option value="on_leave">On Leave</option>
                                            <option value="suspended">Suspended</option>
                                        </select>
                                    </div>
                                </div>
                                <div className={classes.modalInputGroup}>
                                    <label>Notes</label>
                                    <textarea
                                        name="notes"
                                        value={editForm.notes || ""}
                                        onChange={handleEditChange}
                                        rows="3"
                                        disabled={isSaving}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={classes.modalFooter}>
                            <button className={classes.modalBtnGhost} onClick={closeAllModals} disabled={isSaving}>
                                <FaTimes /> Cancel
                            </button>
                            <button className={classes.modalBtnPrimary} onClick={handleSaveEdit} disabled={isSaving}>
                                {isSaving ? (
                                    <><FaSpinner className={classes.spin} /> Saving...</>
                                ) : (
                                    <><FaSave /> Save Changes</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ================= DELETE MODAL ================= */}
            {deleteModalOpen && selectedMember && (
                <div className={classes.modalOverlay} onClick={closeAllModals}>
                    <div className={classes.modalSmall} onClick={(e) => e.stopPropagation()}>
                        <div className={classes.deleteHeader}>
                            <div className={classes.deleteIconWrapper}>
                                <FaExclamationTriangle />
                            </div>
                            <h3>Delete Staff Member?</h3>
                            <p>
                                You are about to permanently delete this staff member.
                                This action cannot be undone.
                            </p>
                        </div>

                        <div className={classes.deleteBody}>
                            <div className={classes.deleteMemberPreview}>
                                <img
                                    src={getAvatar(selectedMember)}
                                    alt={getFullName(selectedMember)}
                                    className={classes.deleteAvatar}
                                />
                                <div>
                                    <h4>{getFullName(selectedMember)}</h4>
                                    <p>{safeString(selectedMember.email)}</p>
                                    <p className={classes.deleteId}>
                                        {safeString(selectedMember.staff_id || selectedMember.employee_no)}
                                    </p>
                                </div>
                            </div>

                            {modalError && (
                                <div className={classes.modalError}>
                                    <FaExclamationTriangle /> {modalError}
                                </div>
                            )}
                        </div>

                        <div className={classes.deleteFooter}>
                            <button
                                className={classes.modalBtnGhost}
                                onClick={closeAllModals}
                                disabled={isDeleting}
                            >
                                <FaUndo /> Cancel
                            </button>
                            <button
                                className={classes.modalBtnDanger}
                                onClick={handleConfirmDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <><FaSpinner className={classes.spin} /> Deleting...</>
                                ) : (
                                    <><FaTrash /> Yes, Delete</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Staff;